import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const coreFeatures = [
  {
    icon: '👤',
    title: 'Patient Management',
    desc: 'Complete digital patient records. Medical history, visit logs, prescriptions, and documents — all searchable, all secure, all in one place.',
  },
  {
    icon: '📅',
    title: 'Smart Appointments',
    desc: 'Intelligent scheduling that eliminates double bookings, sends automated reminders, and manages multi-doctor calendars effortlessly.',
  },
  {
    icon: '💊',
    title: 'Digital Prescriptions',
    desc: 'Generate professional prescriptions digitally. Auto-suggest medicines, flag drug interactions, and maintain a complete prescription history.',
  },
  {
    icon: '🧾',
    title: 'Billing & Invoicing',
    desc: 'One-click billing, GST-compliant invoices, insurance claim tracking, and payment reconciliation — zero paperwork, zero errors.',
  },
  {
    icon: '💊',
    title: 'Pharmacy Module',
    desc: 'Integrated pharmacy inventory, stock alerts, expiry tracking, and direct prescription-to-dispensing workflow for clinic pharmacies.',
  },
  {
    icon: '📊',
    title: 'Analytics Dashboard',
    desc: 'Real-time clinic performance. Patient footfall trends, revenue reports, doctor productivity, and operational bottleneck identification.',
  },
  {
    icon: '🔔',
    title: 'Automated Follow-Ups',
    desc: 'Never lose a patient to silence. Automated WhatsApp and SMS follow-ups for post-visit care, test reminders, and appointment recall.',
  },
  {
    icon: '☁️',
    title: 'Cloud & Multi-Device',
    desc: 'Access your clinic from anywhere. Works on desktop, tablet, and mobile. Data synced in real time across all your staff and locations.',
  },
  {
    icon: '🔒',
    title: 'Enterprise Security',
    desc: 'AES-256 encrypted patient data, role-based access control, audit logs, and full DPDP Act 2023 compliance. Your data. Always yours.',
  },
];

const nexusFeatures = [
  {
    icon: '✅',
    title: 'Verified Network',
    desc: 'Connect with Allopathy, Ayurveda, and Homeopathy practitioners. No spam — only verified medical professionals with credential-checked profiles.',
  },
  {
    icon: '🤝',
    title: 'Visiting Consultants',
    desc: 'Post requirements or find clinics. Manage visiting doctor connections with a flat ₹150 convenience fee per accepted opportunity.',
  },
  {
    icon: '↗️',
    title: 'Structured Referrals',
    desc: 'Refer patients to trusted specialists or get referred. Deep operational integration with HealHub makes partnerships seamless and trackable.',
  },
  {
    icon: '🔐',
    title: 'Professional Discussions',
    desc: 'Securely discuss case studies and post professional requirements in private threads. Confidential, peer-reviewed, professionally structured.',
  },
  {
    icon: '🌉',
    title: 'Cross-System Bridge',
    desc: 'A unique bridge between Allopathic and Ayurvedic practitioners. Every referral and collaboration tracked inside your HealHub dashboard.',
  },
  {
    icon: '📈',
    title: 'Growth Intelligence',
    desc: 'Track referral networks, collaboration history, and professional relationships that actively grow your clinic\'s patient base.',
  },
];

const stats = [
  { value: '500+', label: 'Clinics Onboarded' },
  { value: '99.9%', label: 'Uptime Guaranteed' },
  { value: '₹0', label: 'Setup Cost' },
  { value: '48hr', label: 'Onboarding Time' },
];

const pricingPlans = [
  {
    name: 'Starter',
    price: '₹999',
    period: '/month',
    desc: 'Perfect for solo practitioners and small clinics.',
    features: [
      'Up to 100 patients/month',
      'Appointments & scheduling',
      'Digital prescriptions',
      'Basic billing',
      'Email support',
    ],
    cta: 'Start Free Trial',
    href: 'https://healhub.in',
    highlight: false,
  },
  {
    name: 'Professional',
    price: '₹2,499',
    period: '/month',
    desc: 'For growing clinics with multiple doctors.',
    features: [
      'Unlimited patients',
      'Multi-doctor management',
      'Pharmacy module',
      'Advanced analytics',
      'WhatsApp follow-ups',
      'HealHub Nexus access',
      'Priority support',
    ],
    cta: 'Book Free Demo',
    href: 'https://healhub.in',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For hospital chains and multi-location networks.',
    features: [
      'Multi-location management',
      'Custom integrations',
      'Dedicated account manager',
      'White-label options',
      'SLA guarantees',
      'Full Nexus enterprise suite',
    ],
    cta: 'Talk to Us',
    href: '/contact',
    highlight: false,
  },
];

const howItWorks = [
  {
    step: '01',
    title: 'Book a Free Demo',
    desc: 'Schedule a 30-minute live demo. Our team walks you through the entire platform tailored to your clinic type.',
  },
  {
    step: '02',
    title: 'We Set Everything Up',
    desc: 'Our onboarding team migrates your existing patient data, configures your workflows, and trains your staff — in 48 hours.',
  },
  {
    step: '03',
    title: 'Go Live With Confidence',
    desc: 'Your clinic runs on HealHub from day one. We stay with you through the first month of operations.',
  },
];

export default function HealHub() {
  const [activeTab, setActiveTab] = useState<'healhub' | 'nexus'>('healhub');

  return (
    <div className="page-wrapper" style={{ paddingTop: '80px' }}>
      <SEOHead
        title="HealHub — Complete Clinic Management Software for India"
        description="HealHub is India's most complete clinic management platform. Patient records, appointments, billing, prescriptions, pharmacy, and analytics — all in one place. Trusted by 500+ clinics."
        canonical="/healhub"
        keywords="clinic management software India, HealHub, digital prescription software, hospital management system, clinic billing software, patient management India"
      />

      {/* ── HERO ── */}
      <section
        style={{
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          padding: '80px 32px',
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%,
              rgba(37,99,235,0.45) 0%,
              rgba(29,58,138,0.25) 40%,
              transparent 70%),
            var(--black)
          `,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decorations */}
        <div style={{
          position: 'absolute', top: '-150px', right: '-150px',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.2) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-100px', left: '-100px',
          width: '400px', height: '400px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', position: 'relative' }}>
          <div className="healhub-hero-grid">

            {/* Left — Text */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
                <span className="badge">
                  🏥 &nbsp; Healthcare Software
                </span>
                <span style={{
                  fontSize: '11px', fontWeight: '700',
                  color: '#22C55E',
                  background: 'rgba(34,197,94,0.1)',
                  border: '1px solid rgba(34,197,94,0.3)',
                  padding: '6px 14px', borderRadius: '50px',
                  letterSpacing: '1px', textTransform: 'uppercase',
                }}>● Live Product</span>
              </div>

              <h1 className="display-heading" style={{ marginBottom: '24px' }}>
                HEAL<span className="gradient-text">HUB</span>
              </h1>

              <p style={{
                fontSize: 'clamp(18px, 2.5vw, 24px)',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: '1.5',
                fontWeight: '300',
                marginBottom: '16px',
                maxWidth: '560px',
              }}>
                The complete operating system for your clinic.
              </p>

              <p style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.45)',
                lineHeight: '1.75',
                maxWidth: '520px',
                marginBottom: '40px',
              }}>
                From the moment a patient books an appointment to the final billing and follow-up — 
                HealHub manages every single touchpoint. Trusted by 500+ clinics across India.
              </p>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '48px' }}>
                <a
                  href="https://healhub.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Book Free Demo ↗
                </a>
                <a
                  href="https://healhub.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                >
                  Visit HealHub.in ↗
                </a>
              </div>

              {/* Trust indicators */}
              <div style={{
                display: 'flex', gap: '32px', flexWrap: 'wrap',
              }}>
                {[
                  { label: '500+ Clinics', icon: '🏥' },
                  { label: 'DPDP Compliant', icon: '🔒' },
                  { label: '48hr Onboarding', icon: '⚡' },
                ].map((item) => (
                  <div key={item.label} style={{
                    display: 'flex', alignItems: 'center',
                    gap: '8px', fontSize: '13px',
                    color: 'rgba(255,255,255,0.45)',
                  }}>
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Visual Card */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{
                width: '100%', maxWidth: '480px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: '24px',
                padding: '40px',
                position: 'relative',
              }}>
                {/* Top bar simulation */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  marginBottom: '28px',
                }}>
                  {['#FF5F57', '#FFBD2E', '#28C840'].map((c) => (
                    <div key={c} style={{
                      width: '12px', height: '12px',
                      borderRadius: '50%', background: c,
                    }} />
                  ))}
                  <div style={{
                    flex: 1, height: '1px',
                    background: 'rgba(255,255,255,0.06)',
                    marginLeft: '8px',
                  }} />
                </div>

                {/* Dashboard preview items */}
                <p style={{
                  fontSize: '11px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '2px', textTransform: 'uppercase',
                  marginBottom: '16px',
                }}>Today's Overview</p>

                {[
                  { label: 'Patients Today', value: '34', color: '#3B82F6' },
                  { label: 'Appointments Pending', value: '12', color: '#F59E0B' },
                  { label: 'Prescriptions Issued', value: '28', color: '#22C55E' },
                  { label: 'Revenue Today', value: '₹18,400', color: '#A855F7' },
                ].map((item) => (
                  <div key={item.label} style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 16px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: '10px',
                    marginBottom: '8px',
                  }}>
                    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
                      {item.label}
                    </span>
                    <span style={{
                      fontSize: '16px', fontWeight: '800',
                      fontFamily: 'Barlow Condensed, sans-serif',
                      color: item.color,
                    }}>
                      {item.value}
                    </span>
                  </div>
                ))}

                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  background: 'rgba(37,99,235,0.08)',
                  border: '1px solid rgba(37,99,235,0.2)',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center', gap: '12px',
                }}>
                  <div style={{
                    width: '36px', height: '36px',
                    background: 'rgba(37,99,235,0.2)',
                    borderRadius: '8px',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '18px',
                    flexShrink: 0,
                  }}>🔔</div>
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#fff', marginBottom: '2px' }}>
                      3 follow-up reminders sent
                    </p>
                    <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                      Automated via HealHub
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section style={{
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(59,130,246,0.12)',
        borderBottom: '1px solid rgba(59,130,246,0.12)',
        padding: '0 32px',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}
          className="healhub-stats-grid">
          {stats.map((stat, i) => (
            <div key={i} style={{
              padding: '40px 24px', textAlign: 'center',
              borderRight: i < 3 ? '1px solid rgba(59,130,246,0.12)' : 'none',
            }}>
              <p className="number-display" style={{ marginBottom: '8px' }}>{stat.value}</p>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.5px' }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── TAB SWITCHER — HealHub vs Nexus ── */}
      <section style={{ padding: '80px 32px 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="section-label">The Platform</span>
            <h2 className="section-title" style={{ marginBottom: '16px' }}>
              ONE SUBSCRIPTION.<br />
              <span className="gradient-text">TWO POWERFUL PLATFORMS.</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto 40px' }}>
              HealHub is not just a clinic management tool. It comes bundled with 
              HealHub Nexus — the professional network built exclusively for doctors.
            </p>

            {/* Tab Buttons */}
            <div style={{
              display: 'inline-flex',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(59,130,246,0.15)',
              borderRadius: '50px', padding: '6px', gap: '4px',
            }}>
              {[
                { key: 'healhub', label: '🏥  HealHub Core' },
                { key: 'nexus', label: '🌐  HealHub Nexus' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as 'healhub' | 'nexus')}
                  style={{
                    padding: '12px 28px',
                    borderRadius: '50px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '700',
                    fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.3s',
                    background: activeTab === tab.key
                      ? 'var(--blue-electric)'
                      : 'transparent',
                    color: activeTab === tab.key
                      ? '#fff'
                      : 'rgba(255,255,255,0.45)',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES GRID (conditional) ── */}
      <section style={{ padding: '48px 32px 120px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>

          {activeTab === 'healhub' && (
            <>
              <div style={{
                display: 'grid',
                gap: '2px',
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid rgba(59,130,246,0.12)',
                borderRadius: '20px',
                overflow: 'hidden',
              }} className="features-grid-3">
                {coreFeatures.map((feature, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '40px 36px',
                      background: 'rgba(5,8,16,0.85)',
                      transition: 'background 0.3s',
                      borderRight: i % 3 !== 2
                        ? '1px solid rgba(59,130,246,0.08)' : 'none',
                      borderBottom: i < 6
                        ? '1px solid rgba(59,130,246,0.08)' : 'none',
                    }}
                    onMouseEnter={e =>
                      (e.currentTarget.style.background = 'rgba(59,130,246,0.07)')
                    }
                    onMouseLeave={e =>
                      (e.currentTarget.style.background = 'rgba(5,8,16,0.85)')
                    }
                  >
                    <div style={{
                      width: '52px', height: '52px',
                      background: 'rgba(59,130,246,0.1)',
                      border: '1px solid rgba(59,130,246,0.2)',
                      borderRadius: '12px',
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px', marginBottom: '20px',
                    }}>
                      {feature.icon}
                    </div>
                    <h3 style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '20px', fontWeight: '900',
                      color: '#fff', textTransform: 'uppercase',
                      marginBottom: '12px', letterSpacing: '0.3px',
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.5)',
                      lineHeight: '1.65',
                    }}>
                      {feature.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div style={{ textAlign: 'center', marginTop: '48px' }}>
                <a
                  href="https://healhub.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-blue"
                >
                  Explore All Features at HealHub.in ↗
                </a>
              </div>
            </>
          )}

          {activeTab === 'nexus' && (
            <>
              {/* Nexus Intro Banner */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(59,130,246,0.05) 100%)',
                border: '1px solid rgba(59,130,246,0.25)',
                borderRadius: '20px',
                padding: '48px',
                marginBottom: '32px',
                display: 'flex',
                alignItems: 'center',
                gap: '32px',
                flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: '280px' }}>
                  <span className="section-label" style={{ marginBottom: '12px' }}>
                    Included in HealHub Subscription
                  </span>
                  <h3 style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: 'clamp(28px, 4vw, 44px)',
                    fontWeight: '900', color: '#fff',
                    textTransform: 'uppercase',
                    lineHeight: 1.05, marginBottom: '16px',
                  }}>
                    THE LINKEDIN<br />
                    <span className="gradient-text">FOR DOCTORS</span>
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: '1.7',
                    maxWidth: '480px',
                  }}>
                    Clinics often work in isolation. HealHub Nexus breaks those walls. 
                    A professional network built exclusively for verified medical practitioners — 
                    structured collaboration, referral management, and professional growth.
                  </p>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <div style={{
                    background: 'rgba(59,130,246,0.1)',
                    border: '1px solid rgba(59,130,246,0.25)',
                    borderRadius: '16px', padding: '24px 32px',
                    textAlign: 'center',
                  }}>
                    <p style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '48px', fontWeight: '900', color: '#fff',
                      lineHeight: 1, marginBottom: '8px',
                    }}>✅ LIVE</p>
                    <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px' }}>
                      APPLY FOR ACCESS
                    </p>
                  </div>
                </div>
              </div>

              {/* Nexus Features Grid */}
              <div style={{
                display: 'grid',
                gap: '2px',
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid rgba(59,130,246,0.12)',
                borderRadius: '20px',
                overflow: 'hidden',
              }} className="features-grid-3">
                {nexusFeatures.map((feature, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '40px 36px',
                      background: 'rgba(5,8,16,0.85)',
                      transition: 'background 0.3s',
                      borderRight: i % 3 !== 2
                        ? '1px solid rgba(59,130,246,0.08)' : 'none',
                      borderBottom: i < 3
                        ? '1px solid rgba(59,130,246,0.08)' : 'none',
                    }}
                    onMouseEnter={e =>
                      (e.currentTarget.style.background = 'rgba(59,130,246,0.07)')
                    }
                    onMouseLeave={e =>
                      (e.currentTarget.style.background = 'rgba(5,8,16,0.85)')
                    }
                  >
                    <div style={{
                      width: '52px', height: '52px',
                      background: 'rgba(59,130,246,0.1)',
                      border: '1px solid rgba(59,130,246,0.2)',
                      borderRadius: '12px',
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px', marginBottom: '20px',
                    }}>
                      {feature.icon}
                    </div>
                    <h3 style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '20px', fontWeight: '900',
                      color: '#fff', textTransform: 'uppercase',
                      marginBottom: '12px', letterSpacing: '0.3px',
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.5)',
                      lineHeight: '1.65',
                    }}>
                      {feature.desc}
                    </p>
                  </div>
                ))}
              </div>

              {/* How It Works */}
              <div style={{ marginTop: '64px' }}>
                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '32px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '32px', textAlign: 'center',
                }}>
                  HOW NEXUS WORKS
                </h3>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '24px',
                }} className="nexus-how-grid">
                  {[
                    {
                      step: '01',
                      title: 'Verified Identity',
                      desc: 'Every doctor profile is checked for credentials before access is granted. No imposters, no spam.',
                    },
                    {
                      step: '02',
                      title: 'Cross-System Referral',
                      desc: 'A unique bridge between Allopathic and Ayurvedic practitioners. Referrals tracked inside HealHub.',
                    },
                    {
                      step: '03',
                      title: 'Professional Discussions',
                      desc: 'Securely discuss case studies and post professional requirements in private threads.',
                    },
                  ].map((item, i) => (
                    <div key={i} style={{
                      padding: '32px',
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(59,130,246,0.12)',
                      borderRadius: '16px',
                    }}>
                      <p style={{
                        fontFamily: 'Barlow Condensed, sans-serif',
                        fontSize: '48px', fontWeight: '900',
                        color: 'rgba(255,255,255,0.06)',
                        lineHeight: 1, marginBottom: '12px',
                      }}>{item.step}</p>
                      <h4 style={{
                        fontFamily: 'Barlow Condensed, sans-serif',
                        fontSize: '20px', fontWeight: '900',
                        color: '#fff', textTransform: 'uppercase',
                        marginBottom: '10px',
                      }}>{item.title}</h4>
                      <p style={{
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.45)',
                        lineHeight: '1.65',
                      }}>{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ textAlign: 'center', marginTop: '48px' }}>
                <a
                  href="https://healhub.in/nexus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-blue"
                >
                  Apply for Professional Access ↗
                </a>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Getting Started</span>
            <h2 className="section-title">
              UP AND RUNNING<br />
              <span className="gradient-text">IN 48 HOURS</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gap: '24px' }} className="how-works-grid">
            {howItWorks.map((item, i) => (
              <div key={i} style={{
                display: 'flex', gap: '32px', alignItems: 'flex-start',
                padding: '40px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(59,130,246,0.12)',
                borderRadius: '20px',
                transition: 'all 0.3s',
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(59,130,246,0.06)';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.25)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.12)';
                }}
              >
                <p style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '72px', fontWeight: '900',
                  color: 'rgba(255,255,255,0.06)',
                  lineHeight: 1, flexShrink: 0,
                  minWidth: '80px',
                }}>
                  {item.step}
                </p>
                <div>
                  <h3 style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '28px', fontWeight: '900',
                    color: '#fff', textTransform: 'uppercase',
                    marginBottom: '12px',
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    color: 'rgba(255,255,255,0.5)',
                    lineHeight: '1.7',
                    maxWidth: '560px',
                  }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Transparent Pricing</span>
            <h2 className="section-title" style={{ marginBottom: '16px' }}>
              PLANS FOR EVERY<br />
              <span className="gradient-text">CLINIC SIZE</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              No setup fees. No hidden charges. Cancel anytime.
              HealHub Nexus is included in all paid plans.
            </p>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px', alignItems: 'start',
          }} className="pricing-grid">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                style={{
                  padding: '40px 36px',
                  background: plan.highlight
                    ? 'linear-gradient(135deg, rgba(37,99,235,0.2) 0%, rgba(59,130,246,0.08) 100%)'
                    : 'rgba(255,255,255,0.02)',
                  border: plan.highlight
                    ? '1px solid rgba(59,130,246,0.4)'
                    : '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '20px',
                  position: 'relative',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.5)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {plan.highlight && (
                  <div style={{
                    position: 'absolute', top: '-14px',
                    left: '50%', transform: 'translateX(-50%)',
                    background: 'var(--blue-electric)',
                    color: '#fff', fontSize: '11px',
                    fontWeight: '700', letterSpacing: '1px',
                    textTransform: 'uppercase',
                    padding: '6px 20px', borderRadius: '50px',
                    whiteSpace: 'nowrap',
                  }}>
                    ⭐ Most Popular
                  </div>
                )}

                <p style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '22px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '8px',
                }}>
                  {plan.name}
                </p>
                <p style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.4)',
                  marginBottom: '24px', lineHeight: '1.5',
                }}>
                  {plan.desc}
                </p>

                <div style={{
                  display: 'flex', alignItems: 'flex-end',
                  gap: '4px', marginBottom: '32px',
                }}>
                  <span style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '52px', fontWeight: '900',
                    color: '#fff', lineHeight: 1,
                  }}>
                    {plan.price}
                  </span>
                  <span style={{
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.4)',
                    paddingBottom: '8px',
                  }}>
                    {plan.period}
                  </span>
                </div>

                <div style={{
                  height: '1px',
                  background: 'rgba(59,130,246,0.15)',
                  marginBottom: '24px',
                }} />

                <div style={{
                  display: 'flex', flexDirection: 'column',
                  gap: '12px', marginBottom: '32px',
                }}>
                  {plan.features.map((feature) => (
                    <div key={feature} style={{
                      display: 'flex', alignItems: 'flex-start',
                      gap: '10px', fontSize: '14px',
                      color: 'rgba(255,255,255,0.6)',
                    }}>
                      <span style={{
                        color: '#22C55E', fontSize: '16px',
                        flexShrink: 0, lineHeight: 1.4,
                      }}>✓</span>
                      {feature}
                    </div>
                  ))}
                </div>

                {plan.href.startsWith('http') ? (
                  <a
                    href={plan.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={plan.highlight ? 'btn-blue' : 'btn-outline'}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    {plan.cta} ↗
                  </a>
                ) : (
                  <Link
                    to={plan.href}
                    className={plan.highlight ? 'btn-blue' : 'btn-outline'}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    {plan.cta} →
                  </Link>
                )}
              </div>
            ))}
          </div>

          <p style={{
            textAlign: 'center', marginTop: '32px',
            fontSize: '13px', color: 'rgba(255,255,255,0.3)',
          }}>
            All plans include HealHub Nexus access. Prices are indicative — 
            visit healhub.in for current pricing and offers.
          </p>
        </div>
      </section>

      {/* ── COMPLIANCE BANNER ── */}
      <section style={{
        padding: '64px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{
            display: 'grid', gap: '32px',
            alignItems: 'center',
          }} className="compliance-grid">
            <div>
              <span className="section-label">Built for India</span>
              <h3 style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: 'clamp(28px, 4vw, 44px)',
                fontWeight: '900', color: '#fff',
                textTransform: 'uppercase',
                lineHeight: 1.05, marginBottom: '16px',
              }}>
                SECURE, COMPLIANT,<br />
                <span className="gradient-text">INDIA-FIRST</span>
              </h3>
              <p style={{
                fontSize: '15px',
                color: 'rgba(255,255,255,0.5)',
                lineHeight: '1.7', maxWidth: '520px',
              }}>
                Built in compliance with India's Digital Personal Data Protection Act 2023. 
                Patient data is stored on Indian servers, encrypted with AES-256, and 
                never shared with third parties.
              </p>
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: '16px',
            }}>
              {[
                { icon: '🔒', label: 'AES-256 Encryption' },
                { icon: '🇮🇳', label: 'Indian Server Hosting' },
                { icon: '📋', label: 'DPDP Act 2023 Compliant' },
                { icon: '🛡️', label: 'Role-Based Access Control' },
              ].map((item) => (
                <div key={item.label} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '16px 20px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(59,130,246,0.12)',
                  borderRadius: '12px',
                  fontSize: '13px', color: 'rgba(255,255,255,0.6)',
                  fontWeight: '500',
                }}>
                  <span style={{ fontSize: '20px' }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--black)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <span style={{ fontSize: '64px', display: 'block', marginBottom: '24px' }}>🏥</span>
          <h2 className="section-title" style={{ marginBottom: '20px' }}>
            READY TO MODERNISE<br />
            <span className="gradient-text">YOUR CLINIC?</span>
          </h2>
          <p className="section-subtitle" style={{
            margin: '0 auto 40px',
            fontSize: '17px',
          }}>
            Join 500+ clinics already running on HealHub. 
            Book a free demo today — no credit card required. 
            We set everything up for you.
          </p>
          <div style={{
            display: 'flex', gap: '16px',
            justifyContent: 'center', flexWrap: 'wrap',
          }}>
            <a
              href="https://healhub.in"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Book Free Demo at HealHub.in ↗
            </a>
            <Link to="/contact" className="btn-outline">
              Talk to Our Team →
            </Link>
          </div>
        </div>
      </section>

      {/* ── RESPONSIVE ── */}
      <style>{`
        .healhub-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .healhub-stats-grid {
          grid-template-columns: repeat(4, 1fr);
        }
        .features-grid-3 {
          grid-template-columns: repeat(3, 1fr);
        }
        .nexus-how-grid {
          grid-template-columns: repeat(3, 1fr);
        }
        .how-works-grid {
          grid-template-columns: 1fr;
        }
        .pricing-grid {
          grid-template-columns: repeat(3, 1fr);
        }
        .compliance-grid {
          grid-template-columns: 1fr 1fr;
        }
        @media (max-width: 1024px) {
          .features-grid-3 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .nexus-how-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .pricing-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .healhub-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .healhub-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .features-grid-3 {
            grid-template-columns: 1fr !important;
          }
          .nexus-how-grid {
            grid-template-columns: 1fr !important;
          }
          .pricing-grid {
            grid-template-columns: 1fr !important;
          }
          .compliance-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}